import './AnchorableDrawer.scss';

import { PushpinOutlined } from '@ant-design/icons';
import { Button, Divider, Drawer, Flex } from 'antd';
import { DrawerProps } from 'antd/lib';
import { CSSProperties, useEffect, useState } from 'react';

interface AnchorableDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorableDrawer: boolean;
  setAnchorableDrawer: (anchorable: boolean) => void;
  title: React.ReactNode;
  className: string;
  classNames: DrawerProps['classNames'];
  width?: number | string;
  children: React.ReactNode;
}

/**
 * Drawer que puede anclarse en la pantalla.
 * Para que funcione correctamente el desplazamiento al estar anclado, se debe utilizar una clase en el componente que se desea
 * desplazar de la siguiente manera:
 * .AnchorableDrawerContainer:has(.AnchorableDrawer--anchored) {
 *    margin-right: 500px;
 * }.
 */
const AnchorableDrawer = (props: AnchorableDrawerProps) => {
  const {
    open,
    setOpen,
    anchorableDrawer,
    setAnchorableDrawer,
    title,
    className,
    classNames,
    width,
    children,
  } = props;
  const [scrollOffset, setScrollOffset] = useState(0);
  const drawerRootStyle: CSSProperties = anchorableDrawer
    ? { position: 'fixed', top: 64, right: 0, height: 'calc(100dvh - 64px)', zIndex: 100 }
    : {
        position: 'absolute',
        top: scrollOffset,
        right: 0,
        height: 'calc(100dvh - 64px)',
        zIndex: 10,
      };

  useEffect(() => {
    if (!open || anchorableDrawer) {
      return;
    }

    const mainElement = document.querySelector('main.ant-layout-content');

    setScrollOffset(mainElement?.scrollTop ?? 0);
  }, [open, anchorableDrawer]);

  return (
    <div
      className={`AnchorableDrawer ${open ? (anchorableDrawer ? 'AnchorableDrawer--anchored' : 'AnchorableDrawer--not-anchored') : ''}`}>
      <Drawer
        classNames={classNames}
        className={className}
        title={
          <Flex align="center">
            <Button
              size="small"
              type={anchorableDrawer ? 'primary' : 'default'}
              onClick={() => setAnchorableDrawer(!anchorableDrawer)}
              icon={<PushpinOutlined />}
            />
            <Divider type="vertical" />
            {title}
          </Flex>
        }
        placement="right"
        width={width}
        mask={anchorableDrawer ? false : true}
        closable={false}
        onClose={() => setOpen(false)}
        open={open}
        getContainer={() => document.querySelector('main.ant-layout-content')!}
        rootStyle={drawerRootStyle}>
        {children}
      </Drawer>
    </div>
  );
};

export default AnchorableDrawer;
